import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { getPath } from "common/routing";
import { useExportPropertyAndParcelInfoMutation } from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { SectionDivider } from "../../../Common/__styles__/FullPageFormLayout";
import { Button } from "../../../Common/Buttons/Button";
import FullPageFormLayout from "../../../Common/FullPageFormLayout";
import { Body, Title } from "../../../Common/Typography";
import { Checkbox } from "../../../Inputs/react-hook-form";
import { Section } from "../../__styles__/Content";
import {
  FormTitleWrapper,
  InputWrapper,
  List,
  ListItem,
} from "./__styles__/Operations";

type ExportPropertyAndParcelInformationFormData = {
  parcelsOnly: boolean;
};

const ExportPropertyAndParcelInformation = () => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const history = useHistory();
  const formMethods = useForm<ExportPropertyAndParcelInformationFormData>({
    defaultValues: {
      parcelsOnly: false,
    },
  });
  const { control, getValues, handleSubmit } = formMethods;

  const [dispatch, { loading }] = useExportPropertyAndParcelInfoMutation({
    onCompleted: () => {
      history.push(getPath("operations"));
      addSuccessToast("Successfully dispatched export");
    },
    onError: () => {
      addErrorToast("Failed to dispatch export");
    },
  });

  const onSubmit = async () => {
    const formValues = getValues();
    await dispatch({
      variables: {
        data: {
          parcelsOnly: formValues.parcelsOnly,
        },
      },
    });
  };

  return (
    <FullPageFormLayout
      subtitle="Export property and parcel information"
      prevLocation={getPath("operations")}
      rightContainer={
        <Button
          size="small"
          variant="primary"
          onPress={() => handleSubmit(onSubmit)()}
          isPending={loading}
          title="Export"
        />
      }
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Section>
            <FormTitleWrapper>
              <Title size="large" type="semiBold">
                Export property and parcel information
              </Title>
            </FormTitleWrapper>
            <Body size={"default"} type={"regular"} color="contentSecondary">
              When the export is complete, you will receive an email with a link
              to download a CSV file.
            </Body>
          </Section>
          <SectionDivider />
          <Section>
            <FormTitleWrapper>
              <Title size={"small"} type={"regular"}>
                Export Options
              </Title>
            </FormTitleWrapper>
            <InputWrapper>
              <Checkbox
                name="parcelsOnly"
                control={control}
                label="Parcels Only"
                description="Export only parcel data, excluding properties. When unchecked, the export will include all properties and any parcels without a property."
              />
            </InputWrapper>
          </Section>
          <SectionDivider />
          <Section>
            <FormTitleWrapper>
              <Title size={"small"} type={"regular"}>
                Included Fields
              </Title>
            </FormTitleWrapper>
            <div>
              <Body size={"small"} type={"emphasis"} color="contentPrimary">
                Property & Parcel Information
              </Body>
              <List>
                <ListItem>Owner Name</ListItem>
                <ListItem>Street Address, City, State, Zip</ListItem>
                <ListItem>Property ID, Parcel Number, Lot, Block</ListItem>
                <ListItem>Improvement Value, Year of Construction</ListItem>
                <ListItem>County, Latitude/Longitude</ListItem>
                <ListItem>Mailing Address</ListItem>
              </List>

              <Body size={"small"} type={"emphasis"} color="contentPrimary">
                Flood Zone & FIRM Data
              </Body>
              <List>
                <ListItem>NFIP FIRM Panel, Zone, SFHA Status</ListItem>
                <ListItem>Base Flood Elevation (BFE), Panel Date</ListItem>
                <ListItem>Floodway Status, Elevation Datum</ListItem>
                <ListItem>Regulatory FIRM Panel, Zone, SFHA Status</ListItem>
                <ListItem>Community Name, Community NFIP ID</ListItem>
              </List>

              <Body size={"small"} type={"emphasis"} color="contentPrimary">
                Building Elevation Data
              </Body>
              <List>
                <ListItem>NFIP Lowest Floor Elevation</ListItem>
                <ListItem>Regulatory Lowest Floor Elevation</ListItem>
              </List>

              <Body size={"small"} type={"emphasis"} color="contentPrimary">
                Additional Information
              </Body>
              <List>
                <ListItem>
                  Property Link (URL to view property details)
                </ListItem>
                <ListItem>Custom FIRM data (if applicable)</ListItem>
              </List>
            </div>
          </Section>
        </form>
      </FormProvider>
    </FullPageFormLayout>
  );
};

export default ExportPropertyAndParcelInformation;
